import { onMounted } from 'vue'
import useScriptLoader from './useScriptLoader'

declare global {
	interface Window {
		PaystackPop?: new () => {
			newTransaction: (options: Record<string, unknown>) => void
		}
	}
}

export const usePaystack = () => {
	const { loadScript } = useScriptLoader()
	const purchasePlanEndpoints = (category: string, productCode?: number): string => {
		const { productCategoryType } = useUtils()
		const categoryType = computed(() => productCategoryType(category, productCode))
		let payloads: Record<string | number, string> = {
			health: API_ENDPOINTS['PurchaseHealthPlan'],
			gadget: API_ENDPOINTS['PurchaseGadget'],
			travel: API_ENDPOINTS['PurchaseTravelPlan']
		}
		if (categoryType.value?.isAuto) {
			payloads = {
				[ProductCodeNames['COMPREHENSIVE_AUTO']]: API_ENDPOINTS['PurchaseComprehensiveAuto'],
				[ProductCodeNames['MINI_COMPREHENSIVE']]: API_ENDPOINTS['PurchaseAutoVehiclesMiniComprehensive'],
				[ProductCodeNames['THIRD_PARTY']]: API_ENDPOINTS['PurchaseAutoVehiclesThirdParty']
			}
			return payloads[Number(productCode || 0)] as string
		}
		return payloads?.[category] ?? ''
	}

	const initialize = (arg: PayStackSchemas): Promise<PaymentCallbackResponse | null> => {
		if (window && typeof window?.PaystackPop !== 'function') {
			useNotification().alert({
				status: 400,
				message: 'Paystack is not available at the moment'
			})
			return Promise.resolve(null)
		}
		const paystack = new window.PaystackPop!()
		return new Promise((resolve, reject) => {
			const reference = () => {
				let text = ''
				let ref = ''

				const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
				for (let i = 0; i < 10; i++) {
					text += possible.charAt(Math.floor(Math.random() * possible.length))
					ref = 'covergenius_' + text + Date.now()
				}
				return { ref }
			}
			paystack.newTransaction({
				...arg,
				key: String(import.meta.env.VITE_PAYSTACK_TEST_PUBLIC),
				amount: Number(arg.amount) * 100,
				ref: reference().ref,
				channels: arg.channels || ['bank', 'card', 'ussd', 'bank_transfer'],
				onSuccess: (arg: PaymentCallbackResponse) => {
					return resolve(arg)
				},
				onCancel: (e: unknown) => {
					useNotification().alert({
						status: 400,
						message: 'Operation Cancelled'
					})
					return reject(e)
				}
			})
		})
	}

	onMounted(() => {
		loadScript('https://js.paystack.co/v2/inline.js')
	})

	return {
		initialize,
		purchasePlanEndpoints
	}
}
